/**
 * ImpactAssessmentSection Component
 * Impact assessment section with tabs for overview and history in an accordion
 */

import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CategoryOverview } from "./CategoryOverview";
import { AssessmentHistory } from "./AssessmentHistory";
import { apiClient } from "../../../services/apiClient";

interface ImpactAssessmentSectionProps {
  onViewPDF: (pdfUrl: string) => void;
  regulationId: string;
  regulationVersion: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const ImpactAssessmentSection: React.FC<ImpactAssessmentSectionProps> = ({
  onViewPDF,
  regulationId,
  regulationVersion,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [assessmentCount, setAssessmentCount] = useState<number | null>(null);
  
  // Fetch assessment history count
  useEffect(() => {
    const fetchHistoryCount = async () => {
      if (!regulationId || !regulationVersion) return;
      try {
        const versionNumber = regulationVersion.startsWith("v") ? regulationVersion.substring(1) : regulationVersion;
        const response = await apiClient.get(`/regulations/${regulationId}/${versionNumber}/assessments/history`);
        if (response.status === "success" && Array.isArray(response.data)) {
          const completedCount = response.data.filter(
            (item: { status?: string }) => {
              const s = (item.status || "").toLowerCase();
              return s === "completed" || s === "success";
            }
          ).length;
          setAssessmentCount(completedCount);
        }
      } catch (error) {
        console.error("Error fetching history count:", error);
      }
    };
    fetchHistoryCount();
  }, [regulationId, regulationVersion, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
        AI Impact Assessment
        </Typography>
        {/* <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
          {assessmentCount !== null
            ? `${assessmentCount} assessments completed for this regulation`
            : "Loading assessments..."}
        </Typography> */}
      </Box>

      <Box>
        {/* Tabs and Refresh Button Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            mb: 3,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "primary.main",
              },
            }}
          >
            <Tab label="Summary" />
            <Tab label="Assessment History" />
          </Tabs>

          <Tooltip title="Refresh">
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{
                color: "#6b7280",
                mb: 0.5,
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                  color: "text.primary",
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {activeTab === 0 && (
          <CategoryOverview
            regulationId={regulationId}
            regulationVersion={regulationVersion}
            key={`overview-${refreshKey}`}
          />
        )}
        {activeTab === 1 && (
          <AssessmentHistory
            onViewDetails={onViewPDF}
            regulationId={regulationId}
            regulationVersion={regulationVersion}
            key={`history-${refreshKey}`}
          />
        )}
      </Box>
    </Box>
  );
};
